import { DateTime } from 'luxon';
import { User } from '../classes/user';
import { TransactionType } from '../enums/transaction';
import { IPayout } from '../interfaces/payout';
import { IReservation } from '../interfaces/reservation';
import { IDeletedAccount } from '../interfaces/response/admin-analytics';
import { IUser } from '../interfaces/user';
import { bookingGroup, fromSeconds } from './booking-analytics';

/**
 * Home's "Deleted accounts to check". Shared by the server
 * (`GET admin/reservation/analytics/deletions`) and its tests.
 */

/** How many days back to check deleted accounts' balances. */
export const BALANCE_CHECK_DAYS = 30;
/** Balance checks read each account's ledgers; cap them so a mass deletion can't stall Home. */
export const MAX_BALANCE_CHECKS = 40;

/** What's left in a deleted account's ledgers. */
export interface LeftoverBalance {
  account: number;
  cash: number;
}

const isoDay = (value: unknown): string | undefined => {
  const d = value ? DateTime.fromISO(String(value).slice(0, 10)) : undefined;
  return d?.isValid ? (d.toISODate() ?? undefined) : undefined;
};
const money = (n: number) => `$${n.toFixed(2)}`;

/** Booked or still waiting on an answer, and not over yet. */
function openStay(r: IReservation, today: string): boolean {
  const group = bookingGroup(r);
  if (group !== 'booked' && group !== 'pending') return false;
  const end = isoDay(r.date?.to) ?? isoDay(r.date?.from);
  return !!end && end >= today;
}

/**
 * Deleted accounts that still need someone to act: an upcoming stay as a guest, guests booked at
 * a listing they hosted, a payout request still pending, or money left in a ledger. Accounts with
 * nothing open are left out. Deleting an account doesn't cancel stays or payout requests (the
 * remove_account procedure leaves both), which is why this exists.
 */
export function deletionIssues(
  deletedUsers: IUser[],
  reservations: IReservation[],
  pendingPayouts: IPayout[],
  balances: Map<number, LeftoverBalance>,
  now: DateTime = DateTime.now(),
): IDeletedAccount[] {
  const today = now.toISODate() as string;
  const rows = new Map<number, IDeletedAccount & { at: number }>();
  for (const u of deletedUsers) {
    if (!u.id) continue;
    const deletedAt = fromSeconds(u.deletedDate);
    rows.set(u.id, {
      id: u.id,
      name: `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || `User #${u.id}`,
      email: u.email || undefined,
      deletedAt: deletedAt?.toISO() ?? undefined,
      issues: [],
      at: deletedAt?.toSeconds() ?? 0,
    });
  }
  for (const r of reservations) {
    if (!openStay(r, today)) continue;
    const from = isoDay(r.date?.from);
    const when = from ? DateTime.fromISO(from).toFormat('LLL d') : 'upcoming';
    const guestId = r.user?.id ?? r.userId;
    const guest = guestId ? rows.get(guestId) : undefined;
    if (guest) guest.issues.push({ kind: 'guest-stay', label: `Booked to stay ${when}`, reservationId: r.id });
    const hostId = r.site?.listing?.user?.id;
    const host = hostId ? rows.get(hostId) : undefined;
    if (host) host.issues.push({ kind: 'host-stay', label: `A guest is booked here ${when}`, reservationId: r.id });
  }
  for (const p of pendingPayouts) {
    const row = p.requestedBy?.id ? rows.get(p.requestedBy.id) : undefined;
    if (row) {
      row.issues.push({
        kind: 'payout',
        label: `Payout request for ${money(p.requestedAmount ?? 0)} still pending`,
      });
    }
  }
  for (const [id, b] of balances) {
    const row = rows.get(id);
    if (!row) continue;
    if (b.account > 0) row.issues.push({ kind: 'balance', label: `${money(b.account)} account balance left` });
    if (b.cash > 0) row.issues.push({ kind: 'balance', label: `${money(b.cash)} CurbNTurf Cash left` });
  }
  return [...rows.values()]
    .filter((r) => r.issues.length)
    .sort((a, b) => b.at - a.at)
    .map(({ at: _at, ...row }) => row);
}

/** Deleted recently enough to have their balances checked, newest first, at most MAX_BALANCE_CHECKS. */
export function recentlyDeleted(users: IUser[], now: DateTime = DateTime.now()): IUser[] {
  const since = now.minus({ days: BALANCE_CHECK_DAYS });
  return users
    .filter((u) => {
      const at = fromSeconds(u.deletedDate);
      return !!u.id && !!at && at >= since;
    })
    .sort((a, b) => (b.deletedDate ?? 0) - (a.deletedDate ?? 0))
    .slice(0, MAX_BALANCE_CHECKS);
}

/**
 * A user's spendable balances, from their ledgers (`accountBalanceTransactions`, `cashTransactions`):
 * the same figures the user page shows (account balance plus what's been paid out of it, and
 * CurbNTurf Cash).
 */
export function leftoverBalance(user: IUser): LeftoverBalance {
  const round = (n: number) => Math.round(n * 100) / 100;
  return {
    account: round(User.getAccountBalance(user, TransactionType.available)),
    cash: round(User.getCurbNTurfCash(user, TransactionType.available)),
  };
}
